/**
 * Resident Management Functions
 *
 * This file contains all the core functions needed for resident management:
 * - Photo display and handling
 * - Family member management (add, edit, delete, detach)
 * - View modal functions
 */

import { residentsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { getResidentPhotoUrl } from './photoUtils';

// =========================
// PHOTO DISPLAY FUNCTIONS
// =========================

/**
 * Helper function to get the correct photo URL for display
 */
export const getPhotoUrl = (resident) => {
  return getResidentPhotoUrl(resident);
};

/**
 * Update resident photo
 */
export const updateResidentPhoto = async (residentId, photoFile) => {
  try {
    const formData = new FormData();
    formData.append('profile_photo', photoFile);
    formData.append('_method', 'PUT');

    const response = await fetch(`${import.meta.env.VITE_API_URL}/residents/${residentId}`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to update photo');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating photo:', error);
    throw error;
  }
};

// =========================
// FAMILY MEMBER FUNCTIONS
// =========================

/**
 * Add new family member to the list
 */
export const addFamilyMember = (familyMembers, setFamilyMembers, newMember) => {
  const member = {
    // Use existing ID if this is an existing resident, otherwise generate temporary ID
    id: newMember.existing_resident_id || Date.now(),
    ...newMember,
    // Mark as new only if it's not an existing resident
    isNew: !newMember.existing_resident_id && !newMember.is_existing_resident
  };

  setFamilyMembers([...familyMembers, member]);
  return member;
};

/**
 * Remove family member from the list
 */
export const removeFamilyMember = (familyMembers, setFamilyMembers, memberId) => {
  const updatedMembers = familyMembers.filter(member => member.id !== memberId);
  setFamilyMembers(updatedMembers);
};

/**
 * Edit family member
 */
export const editFamilyMember = (member, setEditingFamilyMember, setNewFamilyMember, setShowFamilyMemberForm) => {
  console.log('=== EDIT FAMILY MEMBER DEBUG ===');
  console.log('Raw member data:', member);
  console.log('Member fields breakdown:', {
    name: member.name,
    relationship: member.relationship,
    relationship_to_head: member.relationship_to_head,
    gender: member.gender,
    birthdate: member.birthdate,
    birthplace: member.birthplace,
    age: member.age,
    occupation: member.occupation
  });

  setEditingFamilyMember(member);

  const familyMemberData = {
    name: member.name || '',
    first_name: member.first_name || '',
    middle_name: member.middle_name || '',
    last_name: member.last_name || '',
    relationship: member.relationship || member.relationship_to_head || '',
    relationship_to_head: member.relationship_to_head || member.relationship || '',
    age: member.age || '',
    birthdate: member.birthdate || '',
    birthplace: member.birthplace || '',
    gender: member.gender || '',
    occupation: member.occupation || '',
    status: member.status || 'active'
  };

  console.log('Setting familyMemberData:', familyMemberData);
  setNewFamilyMember(familyMemberData);
  setShowFamilyMemberForm(true);
};

/**
 * View family member details
 */
export const viewFamilyMember = (member, setSelectedFamilyMember, setShowFamilyMemberViewModal) => {
  setSelectedFamilyMember(member);
  setShowFamilyMemberViewModal(true);
};

/**
 * Confirm delete family member
 */
export const confirmDeleteFamilyMember = (member, setFamilyMemberToDelete, setShowDeleteFamilyMemberModal) => {
  setFamilyMemberToDelete(member);
  setShowDeleteFamilyMemberModal(true);
};

/**
 * Delete family member (remove from household)
 */
let deletionInProgress = false; // Flag to prevent concurrent deletions

export const deleteFamilyMember = async (
  familyMemberToDelete,
  familyMembers,
  setFamilyMembers,
  setShowDeleteFamilyMemberModal,
  setFamilyMemberToDelete
) => {
  // Prevent concurrent API calls
  if (deletionInProgress) {
    console.warn('Delete family member operation already in progress, ignoring duplicate call');
    return;
  }

  try {
    deletionInProgress = true;
    console.log('Starting delete family member operation for:', familyMemberToDelete);

    if (familyMemberToDelete.isNew) {
      // If it's a new member, just remove from local state
      const updatedMembers = familyMembers.filter(m => m.id !== familyMemberToDelete.id);
      setFamilyMembers(updatedMembers);
      toast.success(`${familyMemberToDelete.name} has been removed from the family.`);
    } else {
      // If it's an existing member, remove from household via API
      console.log('Calling API to remove family member from household:', familyMemberToDelete.id);
      const response = await residentsAPI.deleteFamilyMember(familyMemberToDelete.id);
      console.log('API response for delete family member:', response);

      if (response.data?.success) {
        const updatedMembers = familyMembers.filter(m => m.id !== familyMemberToDelete.id);
        setFamilyMembers(updatedMembers);
        toast.success(`${familyMemberToDelete.name} has been removed from the household successfully.`);
      } else {
        throw new Error(response.data?.message || 'Failed to remove family member from household');
      }
    }

    setShowDeleteFamilyMemberModal(false);
    setFamilyMemberToDelete(null);
    console.log('Delete family member operation completed successfully');
  } catch (error) {
    console.error('Error removing family member from household:', error);
    toast.error(error.message || 'Failed to remove family member from household. Please try again.');
  } finally {
    deletionInProgress = false;
  }
};

/**
 * Cancel delete family member
 */
export const cancelDeleteFamilyMember = (setShowDeleteFamilyMemberModal, setFamilyMemberToDelete) => {
  setShowDeleteFamilyMemberModal(false);
  setFamilyMemberToDelete(null);
};

/**
 * Detach family member from household
 */
export const detachFamilyMember = async (member, familyMembers, setFamilyMembers) => {
  try {
    if (member.isNew) {
      // If it's a new member, just remove from local state
      const updatedMembers = familyMembers.filter(m => m.id !== member.id);
      setFamilyMembers(updatedMembers);
    } else {
      // If it's an existing member, detach via API
      const response = await residentsAPI.detachFamilyMember(member.id);

      if (response.success) {
        const updatedMembers = familyMembers.filter(m => m.id !== member.id);
        setFamilyMembers(updatedMembers);
      } else {
        throw new Error('Failed to detach family member');
      }
    }
  } catch (error) {
    console.error('Error detaching family member:', error);
    alert('Failed to detach family member. Please try again.');
  }
};

/**
 * Confirm detach family member
 */
export const confirmDetachFamilyMember = (member, detachCallback) => {
  const confirmed = window.confirm(
    `Are you sure you want to detach ${member.name} from this household? They will become an independent resident.`
  );

  if (confirmed) {
    detachCallback(member);
  }
};

// =========================
// FAMILY MEMBER CRUD OPERATIONS
// =========================

/**
 * Handle family members creation for new residents
 */
export const handleFamilyMembersCreation = async (householdHeadId, members, headData) => {
  try {
    console.log('Creating family members with head ID:', householdHeadId);
    console.log('Members to create:', members);
    console.log('Head data:', headData);

    const promises = members.map(async (member) => {
      // Check if this is an existing resident being added to household
      if (member.existing_resident_id || member.is_existing_resident) {
        console.log('Assigning existing resident to household:', member);
        // Assign existing resident to household with relationship
        try {
          const response = await residentsAPI.assignToHousehold(member.existing_resident_id, householdHeadId, {
            relationship_to_head: member.relationship_to_head || member.relationship
          });
          console.log('Assignment response:', response);
          return response;
        } catch (error) {
          console.error('Error assigning resident to household:', error);
          throw error;
        }
      } else {
        // Create new resident for family member
        console.log('Creating new resident for family member:', member);

        // Use separate name fields if available, otherwise split full name
        const firstName = member.first_name || member.name?.trim().split(' ')[0] || '';
        const lastName = member.last_name || member.name?.trim().split(' ').slice(1).join(' ') || '';
        const middleName = member.middle_name || '';

        const memberData = {
          // Personal Information
          first_name: firstName,
          middle_name: middleName || null,
          last_name: lastName,
          birthdate: member.birthdate || null,
          birthplace: member.birthplace || null,
          gender: member.gender,
          civil_status: 'single', // Default for family members
          occupation: member.occupation || null,

          // Contact and Address (inherit from head)
          contact_number: null,
          email: null,
          house_number: headData.house_number || null,
          street: headData.street || null,
          purok_zone: headData.purok_zone || null,
          barangay: headData.barangay || 'Barangay 360',
          municipality: headData.municipality || 'Default Municipality',
          province: headData.province || 'Default Province',
          zip_code: headData.zip_code || null,

          // Family relationship
          is_household_head: false,
          household_head_id: householdHeadId,
          relationship_to_head: member.relationship_to_head || member.relationship,

          // Additional required fields
          citizenship: 'Filipino',
          length_of_residency: '1 year',
          voter_status: 'not_registered',
          pwd_status: false,
          senior_citizen_status: false,
          status: 'active'
        };

        console.log('Creating family member with data:', memberData);
        return residentsAPI.create(memberData);
      }
    });

    const results = await Promise.all(promises);
    console.log('Family members created successfully:', results);
    return results;
  } catch (error) {
    console.error('Error creating family members:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
};

/**
 * Handle family members updates for existing residents
 */
export const handleFamilyMembersUpdate = async (householdHeadId, members, headData) => {
  try {
    const createPromises = [];
    const updatePromises = [];

    members.forEach(member => {
      // Check if this is an existing resident being added to household
      if (member.existing_resident_id || member.is_existing_resident) {
        console.log('Assigning existing resident to household:', member);
        // Assign existing resident to household with relationship
        updatePromises.push(
          residentsAPI.assignToHousehold(member.existing_resident_id, householdHeadId, {
            relationship_to_head: member.relationship_to_head || member.relationship
          }).then(response => {
            console.log('Assignment response in update:', response);
            return response;
          }).catch(error => {
            console.error('Error in assignment:', error);
            throw error;
          })
        );
      } else if (member.isNew) {
        // Use separate name fields if available, otherwise split full name
        const firstName = member.first_name || member.name?.trim().split(' ')[0] || '';
        const lastName = member.last_name || member.name?.trim().split(' ').slice(1).join(' ') || '';
        const middleName = member.middle_name || '';

        // Create new family member
        createPromises.push(
          residentsAPI.create({
            // Personal Information
            first_name: firstName,
            middle_name: middleName || null,
            last_name: lastName,
            birthdate: member.birthdate || null,
            birthplace: member.birthplace || null,
            gender: member.gender,
            civil_status: 'single', // Default for family members
            occupation: member.occupation || null,

            // Contact and Address (inherit from head)
            contact_number: null,
            email: null,
            house_number: headData.house_number || null,
            street: headData.street || null,
            purok_zone: headData.purok_zone || null,
            barangay: headData.barangay || 'Barangay 360',
            municipality: headData.municipality || 'Default Municipality',
            province: headData.province || 'Default Province',
            zip_code: headData.zip_code || null,

            // Family relationship
            is_household_head: false,
            household_head_id: householdHeadId,
            relationship_to_head: member.relationship,

            // Additional required fields
            citizenship: 'Filipino',
            length_of_residency: '1 year',
            voter_status: 'not_registered',
            pwd_status: false,
            senior_citizen_status: false,
            status: 'active'
          })
        );
      } else {
        // Update existing family member
        console.log('Updating existing family member:', member);
        console.log('Member relationship fields:', {
          relationship: member.relationship,
          relationship_to_head: member.relationship_to_head
        });

        const updateData = {
          ...member,
          // Ensure relationship_to_head is properly set for database
          relationship_to_head: member.relationship_to_head || member.relationship
        };

        console.log('Sending update data:', updateData);

        updatePromises.push(
          residentsAPI.update(member.id, updateData)
        );
      }
    });

    const createResults = await Promise.all(createPromises);
    const updateResults = await Promise.all(updatePromises);

    return { created: createResults, updated: updateResults };
  } catch (error) {
    console.error('Error updating family members:', error);
    throw error;
  }
};

// =========================
// VIEW MODAL FUNCTIONS
// =========================

/**
 * Show household information modal
 */
export const showHouseholdInfo = (
  resident,
  setSelectedResident,
  setShowHouseholdModal,
  setFamilyMembers
) => {
  setSelectedResident(resident);

  // Load family members if available
  if (resident.family_members) {
    setFamilyMembers(resident.family_members);
  } else if (resident.household_members) {
    setFamilyMembers(resident.household_members);
  } else {
    setFamilyMembers([]);
  }

  setShowHouseholdModal(true);
};

/**
 * Format family member data for display
 */
export const formatFamilyMemberForDisplay = (member) => {
  return {
    id: member.id,
    name: member.name || `${member.first_name} ${member.last_name}`,
    relationship: member.relationship || member.relationship_to_head,
    age: member.age || calculateAge(member.birthdate),
    birthdate: member.birthdate,
    gender: member.gender,
    occupation: member.occupation || 'Not specified',
    status: member.status || 'Active'
  };
};

/**
 * Calculate age from birthdate
 */
const calculateAge = (birthdate) => {
  if (!birthdate) return 'Unknown';

  const today = new Date();
  const birth = new Date(birthdate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

// =========================
// FAMILY MEMBER FORM VALIDATION
// =========================

/**
 * Validate family member form data
 */
export const validateFamilyMember = (memberData) => {
  const errors = {};

  if (!memberData.name || memberData.name.trim() === '') {
    errors.name = 'Full name is required';
  } else if (memberData.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters long';
  }

  if (!memberData.relationship || memberData.relationship.trim() === '') {
    errors.relationship = 'Relationship to head is required';
  }

  if (!memberData.gender || memberData.gender.trim() === '') {
    errors.gender = 'Gender is required';
  }

  if (!memberData.birthdate || memberData.birthdate.trim() === '') {
    errors.birthdate = 'Birthdate is required';
  } else {
    const birthDate = new Date(memberData.birthdate);
    const today = new Date();
    if (birthDate > today) {
      errors.birthdate = 'Birthdate cannot be in the future';
    }
  }

  if (!memberData.birthplace || memberData.birthplace.trim() === '') {
    errors.birthplace = 'Birthplace is required';
  }

  if (memberData.age && (isNaN(memberData.age) || memberData.age < 0 || memberData.age > 150)) {
    errors.age = 'Age must be a valid number between 0 and 150';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Reset family member form
 */
export const resetFamilyMemberForm = (setNewFamilyMember, setEditingFamilyMember, setShowFamilyMemberForm) => {
  setNewFamilyMember({
    name: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    relationship: '',
    relationship_to_head: '',
    age: '',
    birthdate: '',
    birthplace: '',
    gender: '',
    occupation: ''
  });
  setEditingFamilyMember(null);
  setShowFamilyMemberForm(false);
};
